"use client";

import { useState } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { ArrowRight, Users, Newspaper, Sparkles } from "lucide-react";
import { AboutModal } from "@/components/about/AboutModal";
import { ImageGrid } from "../about/AboutComponents";

interface AboutProps {
  onBookingClick: () => void;
}

/**
 * About Section Component
 * Clinic introduction with new copy — warm, medically credible, no founder
 * namedrop in the body text (founders are now featured on the dedicated
 * /founders page). The old doctor cards have been replaced with shortcut
 * cards linking to News & Articles and Services.
 */
export function About({ onBookingClick }: AboutProps) {
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);

  return (
    <section
      id="about"
      className="overflow-hidden py-12 lg:py-16 bg-white"
      aria-labelledby="about-title"
    >
      <div className="container mx-auto px-4">
        <div className="flex flex-col items-start gap-12 md:gap-16 xl:flex-row">
          <ImageGrid />

          <div className="order-1 w-full xl:order-1 xl:w-1/2">
            <div className="bg-rose/10 text-rose text-md mb-6 inline-flex items-center gap-2 rounded-full px-4 py-1.5 font-semibold tracking-wide">
              <Sparkles className="h-4 w-4" aria-hidden="true" />
              About SkinSio
            </div>

            <h2
              id="about-title"
              className="mb-8 text-4xl leading-tight font-bold md:text-5xl"
            >
              Doctor-Led Care,{" "}
              <br className="lg:hidden" />
              <span className="text-golden">Evidence-Based Results</span>
            </h2>

            <div className="text-muted-foreground space-y-6 text-lg leading-relaxed">
              <p>
                At SkinSio Clinics, we believe healthy skin, confident hair,
                and graceful ageing should be guided by science — not trends. As
                a doctor-led medical aesthetic and skin cosmetics clinic in the
                heart of Vadodara, our philosophy is simple: every patient
                deserves{" "}
                <strong className="text-foreground">
                  evidence-based treatments, honest medical advice, and
                  personalised care.
                </strong>
              </p>

              <p>
                Our multidisciplinary team brings together consultant
                dermatologists, aesthetic physicians, clinical cosmetologists,
                hair transplant surgeons, physiotherapists, occupational health
                consultants, and dental professionals — all under one roof.
                Whether you are seeking laser treatments, acne and pigmentation
                solutions, hair restoration, anti-ageing procedures, or
                physiotherapy, your treatment is always delivered under medical
                supervision by qualified healthcare professionals.
              </p>

              <p>
                Every journey at SkinSio begins with a detailed medical
                consultation. We take the time to understand your concerns,
                evaluate your skin or hair scientifically, discuss your goals,
                and design an individualised treatment plan — because no two
                patients are alike, and we never believe in one-size-fits-all
                solutions.
              </p>

              <Button
                variant="link"
                className="text-rose group h-auto p-0 text-lg font-bold hover:underline"
                onClick={() => setIsAboutModalOpen(true)}
              >
                Read More
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Button>
            </div>

            {/* Shortcut cards — News & Articles / Services, each with its own
                accent color (rose / gold) instead of a single flat navy tint */}
            <div className="mt-7 grid gap-6 sm:grid-cols-2">
              <Link
                href="/news-and-articles"
                className="group rounded-2xl border border-rose/20 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:shadow-md hover:border-rose/40"
              >
                <div className="bg-rose/10 text-rose mb-4 flex h-12 w-12 items-center justify-center rounded-full">
                  <Newspaper className="h-6 w-6" aria-hidden="true" />
                </div>
                <h3 className="text-foreground mb-1 text-lg font-bold">
                  News & Articles
                </h3>
                <p className="text-muted-foreground mb-3 text-sm">
                  Skin, hair & aesthetic insights from our medical team
                </p>
                <span className="text-rose inline-flex items-center gap-1 text-sm font-semibold">
                  Explore Articles
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>

              <Link
                href="/services"
                className="group rounded-2xl border border-golden/30 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:shadow-md hover:border-golden/60"
              >
                <div className="bg-golden/10 text-golden mb-4 flex h-12 w-12 items-center justify-center rounded-full">
                  <Sparkles className="h-6 w-6" aria-hidden="true" />
                </div>
                <h3 className="text-foreground mb-1 text-lg font-bold">
                  Our Services
                </h3>
                <p className="text-muted-foreground mb-3 text-sm">
                  Explore our full range of skin, hair & laser treatments
                </p>
                <span className="text-golden inline-flex items-center gap-1 text-sm font-semibold">
                  View Services
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            </div>

            <div className="mt-12 flex flex-wrap gap-4">
              <Button
                size="lg"
                className="ml-2 h-14 rounded-full bg-gradient-to-r from-rose to-golden px-8 text-lg text-white shadow-lg shadow-rose/20 hover:opacity-90 md:ml-1"
                onClick={onBookingClick}
                aria-label="Book a consultation"
              >
                Book Consultation
                <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
              </Button>
              <div
                className="ml-2 flex items-center -space-x-3"
                aria-label="Over 2200 happy patients"
              >
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="border-background bg-rose/10 flex h-10 w-10 items-center justify-center rounded-full border-2 text-[10px] font-bold"
                  >
                    <Users
                      className="text-rose h-4 w-4"
                      aria-hidden="true"
                    />
                  </div>
                ))}
                <span className="text-muted-foreground ml-4 text-sm font-medium">
                  2200+ Happy Patients
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AboutModal open={isAboutModalOpen} onOpenChange={setIsAboutModalOpen} />
    </section>
  );
}
